// S3 — shared helpers for contracts, annexes and salaries.
//
// calcSalary mirrors src/HRApp.Core/Employment/SalaryCalculator.cs (the server is the source of
// truth for stored and listed values; this copy only previews the other value while typing).
// Keep both in step: employee contributions 13.78 % of min(gross, maximum insurable income),
// income tax 10 % of (gross − contributions). Maximum insurable income per year below.

export const EUR_BGN = 1.95583;
const CONTRIB = 0.1378;
const TAX = 0.10;
const MAX_INSURABLE = [
  [2016, 2600, 'BGN'], [2019, 3000, 'BGN'], [2022, 3400, 'BGN'],
  [2024, 3750, 'BGN'], [2025, 4130, 'BGN'], [2026, 2300, 'EUR'],
];
const LAST_KNOWN_YEAR = 2026;

const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;
const convert = (amount, from, to) => (from === to ? amount : from === 'EUR' ? amount * EUR_BGN : amount / EUR_BGN);

export function maxInsurable(year, currency) {
  let row = MAX_INSURABLE[0];
  for (const r of MAX_INSURABLE) if (r[0] <= year) row = r;
  return convert(row[1], row[2], currency);
}

export function fromGross(gross, currency, year) {
  const contributions = round2(Math.min(gross, maxInsurable(year, currency)) * CONTRIB);
  const tax = round2((gross - contributions) * TAX);
  return { gross, net: round2(gross - contributions - tax), contributions, tax, ratesYear: Math.min(year, LAST_KNOWN_YEAR) };
}

export function fromNet(net, currency, year) {
  const cap = maxInsurable(year, currency);
  let gross = net / ((1 - TAX) * (1 - CONTRIB));
  if (gross > cap) gross = net / (1 - TAX) + round2(cap * CONTRIB);
  gross = round2(gross);
  return { ...fromGross(gross, currency, year), net };
}

export function calcSalary(amount, currency, basis, year) {
  const a = Number(amount);
  if (!a || a <= 0) return null;
  return basis === 'Net' ? fromNet(a, currency, year) : fromGross(a, currency, year);
}

export const fmtMoney = (v, cur) =>
  v == null || v === '' ? '—'
    : `${new Intl.NumberFormat('bg-BG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v))}${cur ? ` ${cur}` : ''}`;

// 'yyyy-MM-dd…' → 'dd.MM.yyyy'
export const fmtDate = (d) => {
  if (!d) return '—';
  const s = String(d).substring(0, 10).split('-');
  return s.length === 3 ? `${s[2]}.${s[1]}.${s[0]}` : String(d);
};

// Local calendar date as 'yyyy-MM-dd' (no UTC shift).
export const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const ANNEX_TYPES = [
  { value: 'SalaryChange', label: 'Salary change' },
  { value: 'PositionChange', label: 'Position change' },
  { value: 'RemoteWork', label: 'Remote work' },
  { value: 'Extension', label: 'Extension' },
  { value: 'Other', label: 'Other' },
];
export const annexTypeLabel = (v) => ANNEX_TYPES.find((t) => t.value === v)?.label || v;

// Check codes from the server → MUI chip colour.
export const CHECK_COLORS = {
  LEAVING: 'warning', TERMINATED: 'default', STARTS: 'info',
  DRAFT_PAST_EFFECTIVE: 'error', NO_CONTRACT_FILE: 'default', NO_CONTRACT: 'error',
};

export const apiErrorText = (err, fallback) =>
  err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : null) || fallback;
